<template>
    <div class="row">
        <div class="col-md-12" dusk="ad-set-editor-ready">
            <div class="campaign-box" data-dusk="ad-set-name">
                <name-section v-model.trim="adSet.name" v-cloak>
                    <label v-show="showErrors && !validation.name" class="error" slot="validation">The Ad Set Name field is required.</label>
                    <label v-show="!validation.nameUnique" class="error" slot="validation">The Ad Set Name already exists.</label>
                </name-section>
            </div>
            <span v-if="editing" data-dusk="editing-existing"></span>
        </div>

        <div class="col-md-12" v-if="hasTypeFeatureEnabled && (!editing || hasType)" v-cloak>
            <div v-if="editing" v-show="hasType" class="campaign-box" data-test="ad-set-type">
                <div class="campaign-option">
                    <div class="campaign-option--title">
                        Ad Set Type
                    </div>
                    <div class="campaign-adset-location__input">
                        {{ adSet.type.label }}
                    </div>
                </div>
            </div>
            <div v-else class="campaign-box" data-test="ad-set-type">
                <select-section
                    name="ad-set-type"
                    v-model="adSet.type"
                    :options="dimensions.types"
                    :allow-empty="false"
                    v-validate="'required'"
                    data-vv-as="Ad Set Type">
                    <span slot="title">
                        Ad Set Type
                        <info-tooltip text="Select the type you wish to apply to this ad set."></info-tooltip>
                    </span>
                </select-section>

                <div class="clearfix"></div>
                <label class="error" v-show="vErrors.has('ad-set-type')">{{ vErrors.first('ad-set-type') }}</label>
            </div>
        </div>

        <div class="col-md-12" v-if="showProductTypeSection" v-cloak>
            <div class="campaign-box" data-dusk="ad-set-product-type" data-test="ad-set-product-type">
                <deposits-product-type
                    v-if="showDepositsProductTypeSelect"
                    v-model="adSet.loan_purposes"
                    :editing="editing"
                    :loan-purposes-list="dimensions.loan_purposes">
                    <cd-terms
                        v-model="adSet.terms_cd_months"
                        :cd-terms-list="dimensions.terms_cd">
                    </cd-terms>
                </deposits-product-type>

                <select-section
                    v-if="isMortgage || isHomeEquity || isRealEstate"
                    v-model="adSet.loan_purposes"
                    :options="dimensions.loan_purposes"
                    :multiple="true">
                    <span slot="title">
                        Product Type
                        <info-tooltip text="Select the products you wish to apply to this ad set."></info-tooltip>
                    </span>
                </select-section>

                <div class="clearfix"></div>
                <label v-show="showErrors && !validation.loan_purposes" class="error">The Product Type field is required.</label>
            </div>
        </div>

        <div class="col-md-12" v-if="isMortgage" v-cloak>
            <div class="campaign-box" data-dusk="ad-set-product-family-type" data-test="ad-set-product-family-type">
                <select-section
                    v-model="adSet.product_family_types"
                    :options="dimensions.product_family_types"
                    :multiple="true">
                    <span slot="title">
                        Product Family Type
                        <info-tooltip
                            text="Select the product family type that you wish to apply to this ad set."></info-tooltip>
                    </span>
                </select-section>

                <div class="clearfix"></div>
                <label v-show="showErrors && !validation.product_family_types" class="error">The Product Family Type field is required.</label>
            </div>
        </div>

        <div class="col-md-12" v-if="featureFlag('ad-set-device-types-enabled')" v-cloak>
            <div class="campaign-box" data-dusk="ad-set-device-type" data-test="ad-set-device-type">
                <select-section
                    v-model="adSet.device_types"
                    :options="dimensions.device_types"
                    :multiple="true">
                    <span slot="title">
                        Device Type
                        <info-tooltip
                            text="Select the device type that you wish to apply to this ad set."></info-tooltip>
                    </span>
                </select-section>

                <div class="clearfix"></div>
                <label v-show="showErrors && !validation.device_types" class="error">The Device Type field is required.</label>
            </div>
        </div>

        <div class="col-md-12" v-if="isDeposits">
            <div v-show="showAdSetLocations">
                <div class="campaign-box" v-if="loadedLocationsList">
                    <deposits-state-selector
                        v-model="adSet.locations"
                        ref="deposits-state-select"
                        :locations-list="dimensions.locations"
                        :show-errors="showErrors"
                        :user-roles="userRoles"
                        v-on:validation="setValidation">
                    </deposits-state-selector>
                </div>
                <div v-else>
                    <div class="campaign-box">
                        <div class="campaign-option--title">
                            Select States
                        </div>
                        <div>
                            <label class="error">The States field is required but there are no states assigned to this advertiser</label>
                            <div v-if="canManageStates">
                                <a href="/admin/advertisers/states" target="_blank">Manage States</a>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div v-show="!showAdSetLocations">
                <div class="campaign-box" v-if="loadedLocationsList">
                    <deposits-campaign-adset-preview-summary-row
                        name="States Included"
                        :items="states"
                        label="id"
                    ></deposits-campaign-adset-preview-summary-row>
                </div>
            </div>
        </div>

        <div class="col-md-12" v-if="isMortgage">
            <div class="campaign-box" data-dusk="ad-set-state" v-if="loadedLocationsList">
                <mortgage-state-selector
                    v-model="adSet.locations"
                    :locations-list="mortgageLocations"
                    :show-errors="showErrors"
                    v-on:validation="setValidation">
                </mortgage-state-selector>
            </div>
            <div v-else>
                <div class="campaign-box">
                    <div class="campaign-option--title">
                        Select States
                    </div>
                    <div>
                        <label class="error">The States field is required but there are no states assigned to this advertiser</label>
                        <div v-if="canManageStates">
                            <a href="/admin/advertisers/states" target="_blank">Manage States</a>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="col-md-12" v-if="isHomeEquity">
            <div class="campaign-box" data-dusk="ad-set-state" v-if="loadedLocationsList">
                <mortgage-state-selector
                    v-model="adSet.locations"
                    :locations-list="homeEquityLocations"
                    :show-errors="showErrors"
                    v-on:validation="setValidation">
                </mortgage-state-selector>
            </div>
            <div v-else>
                <div class="campaign-box">
                    <div class="campaign-option--title">
                        Select States
                    </div>
                    <div>
                        <label class="error">The States field is required but there are no states assigned to this advertiser</label>
                        <div v-if="canManageStates">
                            <a href="/admin/advertisers/states" target="_blank">Manage States</a>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="col-md-12" v-if="isRealEstate">
            <div class="campaign-box" data-dusk="ad-set-state">
                <mortgage-state-selector
                    v-model="adSet.locations"
                    :locations-list="dimensions.locations"
                    :show-errors="showErrors"
                    v-on:validation="setValidation">
                </mortgage-state-selector>
            </div>
        </div>

        <div class="col-md-12" v-cloak v-if="showPhoneNumberSection">
            <div class="campaign-box" data-dusk="ad-set-phone-numbers">
                <select-section
                    name="ad-set-phone"
                    v-model="adSet.phone"
                    :searchable="true"
                    :allow-empty="false"
                    :options="formattedPhoneNumbers"
                    v-validate="'required'"
                    data-vv-as="Phone Number">
                    <span slot="title">
                        Phone Number
                        <info-tooltip text="Select a phone number."></info-tooltip>
                    </span>
                </select-section>
                <label class="error" v-show="vErrors.has('ad-set-phone')">{{ vErrors.first('ad-set-phone') }}</label>
                <label class="error" v-if="formattedPhoneNumbers.length === 0">There is no numbers provisioned for you</label>
            </div>
        </div>

        <div class="col-md-12" v-if="showCreativeSection && loadedCreativesList">
            <div class="campaign-box" data-dusk="ad-set-creative">
                <creative-selector
                    v-model="adSet.creative_uuid"
                    :creatives="dimensions.creatives">
                </creative-selector>
            </div>
        </div>

        <div class="col-md-12" v-if="showLandingURL" v-cloak>
            <div class="campaign-box">
                <div class="campaign-option">
                    <div class="campaign-option--title">
                        Landing URL
                        <info-tooltip text="Provides a landing URL that will be used for cost per click"></info-tooltip>
                    </div>
                    <template v-if="adSet.creative_uuid">
                        <div class="m-t-sm">The Creative "Landing URL" field will be used {{ creative && creative.landing_url ? `(${creative.landing_url})` : '' }}</div>
                    </template>
                    <template v-else>
                        <input type="text" class="campaign-adset-name__input text-input"
                               v-model.trim="adSet.landing_url"
                               placeholder="Enter a landing URL for this Ad Set">
                        <label v-show="showErrors && !validation.landing_url" class="error" slot="validation">The Landing URL field is required (when a Creative is not selected).</label>
                    </template>
                </div>
            </div>
        </div>

        <div class="col-md-12" v-if="isMortgage" v-show="enableLoanSizes">
            <div class="campaign-box" dusk="ad-set-loan-size">
                <loan-size-selector
                    v-model="adSet.loan_sizes"
                    :opt-in-new-conforming-limit="adSet.opt_in_new_conforming_limit"
                    @setOptInNewConformingLimit="(value) => adSet.opt_in_new_conforming_limit = value"
                    :loan-sizes="loanSizes">
                </loan-size-selector>
            </div>
        </div>

        <div class="col-md-12" v-show="showTagsSection">
            <div class="campaign-box" dusk="ad-set-tags">
                <tags-selector
                    v-model="adSet.tags"
                    :options="dimensions.tags"
                    data-test="tags-selector">
                </tags-selector>
            </div>
        </div>

        <div class="col-md-12" v-cloak>
            <budget-section
                v-model="adSet"
                :creating="creating"
                :dimensions="dimensions"
                :product-category-id="campaign.product_category.id"
                :validation="validation"
                :show-errors="showErrors"
                :user-roles="userRoles">
            </budget-section>
        </div>

        <div class="col-md-12" v-cloak>
            <schedule-section
                v-model="adSet"
                :validation="validation"
                :show-ad-set-schedule="showAdSetSchedule"
                :show-errors="showErrors"
                :show-dates="true"
                @scheduleHasOverlaps="(hasOverlaps) => $emit('scheduleHasOverlaps', hasOverlaps)">
            </schedule-section>
        </div>

        <div v-if="featureFlag('ad-set-attributes-enabled')" class="col-md-12" v-cloak>
            <div class="campaign-box">
                <div class="campaign-option--title">Attributes</div>
                <template v-if="dimensions.attributes && dimensions.attributes.length > 0">
                    <div class="row m-b-sm" v-show="adSet.attributes && adSet.attributes.length > 0">
                        <div class="col-md-4 p-r-none">
                            Name
                        </div>
                        <div class="col-md-5 p-r-none">
                            Value
                        </div>
                    </div>
                    <div v-for="(attribute, index) in adSet.attributes" class="row m-b-xs">
                        <div class="col-md-4 p-r-none" :data-test="`ad-set-attribute-name-${index}`">
                            <multiselect v-model="attribute.name"
                                         :options="dimensions.attributes"
                                         placeholder="Select attribute"
                                         :show-labels="false"
                                         :searchable="false"
                                         :allow-empty="false">
                            </multiselect>
                        </div>
                        <div class="col-md-5 p-r-none" :data-test="`ad-set-attribute-value-${index}`">
                            <template v-if="getAttributeDataType(attribute.name) === 'boolean'">
                                <div class="p-t-xs">
                                    <br-switch open-value="true" close-value="false" v-model="attribute.value"></br-switch>
                                </div>
                            </template>
                            <template v-else-if="getAttributeDataType(attribute.name) === 'dropdown'">
                                <select id="categories" class="text-input" v-model="attribute.value">
                                    <option v-for="item in getAttributeOptions(attribute.name)" :value="item.key">{{ item.value }}</option>
                                </select>
                            </template>
                            <input v-else type="text" class="text-input" v-model="attribute.value" :disabled="!attribute.name">
                        </div>
                        <div class="col-md-2" :data-test="`ad-set-attribute-delete-btn-${index}`">
                            <button type="button" class="btn btn-block btn-adjusted --gray" @click="removeAttributeRow(index)">Remove</button>
                        </div>
                    </div>
                    <div class="row" v-show="hasDuplicateAttributes">
                        <label class="col-md-12 error m-t-xs">
                            Duplicated attributes are not allowed
                        </label>
                    </div>
                    <div class="row">
                        <div class="col-md-12 m-t-sm">
                            <a @click="addNewAttributeRow" data-test="btn-add-ad-set-attribute" style="cursor: pointer;">+ Add attribute</a>
                        </div>
                    </div>
                </template>
                <template v-else>
                    <div>There are no attributes created for this vertical</div>
                </template>
            </div>
        </div>

        <div v-if="featureFlag('ad-set-zip-codes-enabled')" class="col-md-12" v-cloak>
            <div class="campaign-box">
                <div class="campaign-option--title">Zip Codes</div>
                <div class="row m-t-md m-b-md">
                    <div class="col-md-12">
                        <textarea v-model="adSet.zip_codes" class="form-control text-input" rows="7"></textarea>
                    </div>
                </div>
            </div>
        </div>

        <div v-for="attribute in dimensions.expanded_attributes" class="col-md-12" v-cloak>
            <div class="campaign-box">
                <div class="campaign-option--title">{{ attribute.description }}</div>
                <div class="row m-t-md m-b-md">
                    <div class="col-md-12">
                        <VueTrix v-if="attribute.data_type === 'text-rich'" v-model="adSet.expanded_attributes[attribute.name]"></VueTrix>
                        <textarea v-else-if="attribute.data_type === 'textarea'" v-model="adSet.expanded_attributes[attribute.name]" class="form-control text-input" rows="5"></textarea>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script type="text/babel">
import NameSection from './shared/name.vue';
import MortgageStateSelector from './mortgage/state-selector.vue';
import DepositsStateSelector from './deposits/state-selector.vue';
import LoanSizeSelector from './shared/loan-size-selector.vue';
import TagsSelector from './shared/tags-selector.vue';
import CreativeSelector from './shared/creative-selector.vue';
import SelectSection from './shared/select.vue';
import BudgetSection from './shared/budget.vue';
import ScheduleSection from './shared/schedule.vue';
import { default as store } from 'components/campaigns/manager/store';
import DepositsProductType from './shared/deposits-product-type';
import CdTerms from './shared/cd-terms';
import _ from 'lodash';
import Multiselect from 'vue-multiselect'
import VueTrix from "vue-trix";

export default {
        props:      [
            'value',
            'campaign',
            'dimensions',
            'features',
            'loan-sizes',
            'baseUrl',
            'validation',
            'enable-loan-sizes',
            'showErrors',
            'phoneNumbers',
            'userRoles'
        ],
        data() {
            return {
                adSet:       this.value,
                dateStart:   {
                    time: this.value.start_date
                },
                dateEnd:     {
                    time: this.value.end_date
                },
                showEndDate: false
            };
        },
        watch:      {
            value(val) {
                this.adSet = val;
                this.dateStart.time = this.adSet.start_date;
                this.dateEnd.time = this.adSet.end_date;
            },

            'adSet.locations'(newVal, oldVal) {
                if (newVal.length >= oldVal.length) {
                    return;
                }

                let component = this;

                Object.keys(oldVal).forEach(function(key) {
                    if (_.findIndex(newVal, oldVal[key]) === -1 && component.existLocationOnBid(oldVal[key])) {
                        component.$nextTick(function() {
                            component.adSet.locations = oldVal;
                            sweetAlert.fire(
                                '',
                                oldVal[key].label + ' cannot be removed from this Ad Set until it is removed from your bids.',
                                'error'
                            );
                        });
                    }
                });
            },

            'adSet.loan_purposes'(loanPurpose, _) {
                if (!this.isCertificateOfDeposit) {
                    this.adSet.terms_cd_months = [];
                }
            }
        },
        methods:    {
            setValidation(field, status) {
                this.$emit('validate', field, status);
            },

            existLocationOnBid(location) {
                let component = this,
                    locationExist = false;

                Object.keys(this.adSet.bids).forEach(function(index) {
                    Object.keys(component.adSet.bids[index].items).forEach(function(dimensionName) {
                        if (dimensionName === 'locations' &&
                            _.findIndex(component.adSet.bids[index].items[dimensionName], location) >= 0) {
                            locationExist = true;
                        }
                    });
                });

                return locationExist;
            },

            addNewAttributeRow() {
                this.adSet.attributes.push({
                    name: null,
                    value: null
                });
            },

            removeAttributeRow(index) {
                this.adSet.attributes.splice(index, 1);
            },

            getAttributeDataType(attributeName) {
                const attribute = _.find(this.dimensions.raw_attributes, { name: attributeName });

                return attribute ? attribute.data_type : null;
            },

            getAttributeOptions(attributeName) {
                const attribute = _.find(this.dimensions.raw_attributes, { name: attributeName });

                return attribute?.option_values ? Object.entries(JSON.parse(attribute.option_values))
                    .map(([key, value]) => ({ key, value })) : [];
            },

            filteredLocations(locations) {
                const blockedCPL = ['AK', 'MS', 'NY'];
                const blockedCPC = ['AK'];
                const blocked = this.adSetType === 1 ? blockedCPL : this.adSetType === 3 ? blockedCPC : [];

                locations = locations.filter(location => !blocked.includes(location.id));

                if (this.isMortgage || this.isHomeEquity) {
                    this.adSet.locations = this.adSet.locations.filter(location => !blocked.includes(location.id));
                }

                return locations;
            },
        },
        computed:   {
            mortgageLocations() {
                return this.filteredLocations(this.dimensions.locations);
            },

            homeEquityLocations() {
                return this.filteredLocations(this.dimensions.locations);
            },

            loadedLocationsList() {
                return this.dimensions.locations.length > 0;
            },

            loadedLoanPurposesList() {
                return this.dimensions.loan_purposes.length > 0;
            },

            loadedCreativesList() {
                return this.dimensions.creatives && this.dimensions.creatives.length > 0;
            },

            loadedTermsCdList() {
                return this.dimensions.terms_cd.length > 0;
            },

            formattedPhoneNumbers() {
                return this.phoneNumbers
                    ? this.phoneNumbers.map(item => ({
                        id:    item.leasedPhoneNumber,
                        label: `${item.label || 'N/A'} | ${item.leasedPhoneNumberFormatted} | ${item.advertiserPhoneNumberFormatted}`
                    }))
                    : {}
            },

            isMortgage() {
                return this.campaign.product_category.id === 2;
            },

            isDeposits() {
                return this.campaign.product_category.id === 24;
            },

            isHomeEquity() {
                return this.campaign.product_category.id === 20;
            },

            isRealEstate() {
                return this.campaign.product_category.id === 27;
            },

            isCertificateOfDeposit() {
                return (this.adSet.loan_purposes.length == 1) && (this.adSet.loan_purposes[0].id == 'certificate of deposit');
            },

            isCostPerClick() {
                return store.getters.isCostPerClick;
            },

            isCostPerLead() {
                return store.getters.isCostPerLead;
            },

            creating() {
                return !this.adSet.id;
            },

            editing() {
                return !!this.adSet.id;
            },

            showPhoneNumberSection() {
                return this.features.ad_set.phone && this.adSetType === 2;  // show for adset type Cost Per Call
            },

            showProductTypeSection() {
                return (this.isMortgage || this.isHomeEquity || this.isRealEstate || this.showDepositsProductTypeSelect) && this.loadedLoanPurposesList;
            },

            showDepositsProductTypeSelect() {
                return this.isDeposits && this.loadedTermsCdList && (this.creating || (this.editing && this.adSet.loan_purposes[0].id == 'certificate of deposit'));
            },

            showCreativeSection() {
                return this.isHomeEquity || (this.isMortgage && this.features.creative.mortgage);
            },

            adSetType() {
                return _.get(this.adSet, 'type.id', 0);
            },

            hasType() {
                return this.adSetType > 0;
            },

            hasTypeFeatureEnabled() {
                return (this.isMortgage && this.features.ad_set.type.mortgage) ||
                    (this.isDeposits && this.features.ad_set.type.deposits) ||
                    (this.isHomeEquity && this.features.ad_set.type.he);
            },

            showTagsSection() {
                return this.userRoles.includes('staff') || this.userRoles.includes('engineer');
            },

            allStatesSelected () {
                return this.adSet.locations.length >= 50;
            },

            states () {
                if (this.allStatesSelected) {
                    return [{id: 'All States'}];
                }

                const adSetLocations = this.adSet.locations;
                if (adSetLocations[0] && adSetLocations[0].id === 'PR') {
                    adSetLocations[0].id = 'National';
                    adSetLocations[0].label = 'US';
                }

                return _.orderBy(adSetLocations, [location => location.id.toLowerCase()], ['asc']);
            },

            showAdSetLocations() {
                if (this.featureFlag('deposits.hide-state-picker') !== true) {
                    return true; // everyone sees location picker until feature flag is enabled
                }

                return this.userRoles.includes('staff')
                    || this.userRoles.includes('engineer');
            },

            showAdSetSchedule() {
                return this.adSetType === 2
                    || this.userRoles.includes('staff')
                    || this.userRoles.includes('engineer');
            },

            canManageStates() {
                return this.userRoles.includes('staff')
                    || this.userRoles.includes('engineer');
            },

            showLandingURL() {
                return this.adSetType === 3 && this.featureFlag('ad-set-landing-url-enabled');
            },

            dimensionTypes() {
                const filteredTypes = [];

                if (Number(this.campaign.ad_product.id) === 1) {
                    if (this.adSetType === 1) {
                        filteredTypes.push({ id: 1, label: "Cost per Lead" });
                    }

                    if (this.adSetType === 2) {
                        filteredTypes.push({ id: 2, label: "Cost per Call" });
                    }

                    filteredTypes.push({ id: 3, label: "Cost per Click" });
                } else {
                    filteredTypes.push({ id: 1, label: "Cost per Lead" });
                    filteredTypes.push({ id: 2, label: "Cost per Call" });

                    if (this.adSetType === 3) {
                        filteredTypes.push({ id: 3, label: "Cost per Click" });
                    }
                }

                return filteredTypes;
            },

            hasDuplicateAttributes() {
                const names = [];
                for (const attribute of this.adSet.attributes) {
                    if (!attribute.name) {
                        continue;
                    }

                    if (names.includes(attribute.name)) {
                        this.setValidation('attributes', false);
                        return true;
                    }

                    names.push(attribute.name);
                }

                this.setValidation('attributes', true);
                return false;
            },

            creative() {
                if (!this.adSet.creative_uuid) {
                    return null;
                }

                return _.find(this.dimensions.creatives, { uuid: this.adSet.creative_uuid });
            }
        },
        components: {
            NameSection,
            MortgageStateSelector,
            DepositsStateSelector,
            TagsSelector,
            LoanSizeSelector,
            CreativeSelector,
            SelectSection,
            BudgetSection,
            ScheduleSection,
            DepositsProductType,
            CdTerms,
            Multiselect,
            VueTrix
        }
    };
</script>

<style scoped>
    .btn-adjusted {
        padding: 10px 25px;
    }
</style>

<style>
    .trix-button-group--file-tools,
    .trix-button--icon-link,
    .trix-button--icon-strike,
    .trix-button--icon-heading-1,
    .trix-button--icon-quote,
    .trix-button--icon-code,
    .trix-button--icon-decrease-nesting-level,
    .trix-button--icon-increase-nesting-level
    {
        visibility: hidden;
        display: none;
    }

    .trix-content {
        max-height: 300px;
        overflow-y: auto;
    }
</style>
